import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { MapChampion, Ward, getChampionMS, FLASH_RANGE_GRID, WARD_VISION_RANGE_GRID } from './mapTypes';
import GameAssetsContext from '../../XXX/context/GameAssetContext';
import './PathfindingMap.css';

/* ══════════════════ Constants ══════════════════ */
const GRID_RES = 512;
const API_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/riftmap`;

const CHAMP_HIT_RADIUS = 12;
const WP_HIT_RADIUS = 10;
const WARD_HIT_RADIUS = 8;

/* ── Vision bitmask constants ── */
const VISION_WALL_MASK = (1 << 0) | (1 << 2) | (1 << 3) | (1 << 5); // wall|turret|inhib|nexus
const BUSH_BIT = 1 << 1;

const LAYERS = [
  { bit: 1 << 0,  rgb: [220, 50, 50]   as const, priority: 10, label: 'wall'          },
  { bit: 1 << 1,  rgb: [50, 190, 50]   as const, priority: 4,  label: 'bush'          },
  { bit: 1 << 2,  rgb: [255, 210, 20]  as const, priority: 9,  label: 'turret'        },
  { bit: 1 << 3,  rgb: [180, 60, 255]  as const, priority: 8,  label: 'inhibitor'     },
  { bit: 1 << 4,  rgb: [255, 140, 20]  as const, priority: 6,  label: 'jungleCamp'    },
  { bit: 1 << 5,  rgb: [30, 200, 240]  as const, priority: 7,  label: 'nexus'         },
  { bit: 1 << 6,  rgb: [255, 100, 200] as const, priority: 5,  label: 'spawn'         },
  { bit: 1 << 7,  rgb: [0, 255, 170]   as const, priority: 5,  label: 'minionSpawn'   },
  { bit: 1 << 8,  rgb: [200, 100, 40]  as const, priority: 2,  label: 'campRange'     },
  { bit: 1 << 9,  rgb: [60, 120, 255]  as const, priority: 1,  label: 'river'         },
  { bit: 1 << 10, rgb: [100, 220, 255] as const, priority: 2,  label: 'scuttleArea'   },
  { bit: 1 << 11, rgb: [255, 255, 80]  as const, priority: 1,  label: 'minionPath'    },
  { bit: 1 << 12, rgb: [255, 60, 60]   as const, priority: 3,  label: 'towerRange'    },
  { bit: 1 << 13, rgb: [0, 255, 255]   as const, priority: 2,  label: 'scuttleShrine' },
];

const LAYERS_BY_PRIORITY = [...LAYERS].sort((a, b) => a.priority - b.priority);

/* ══════════════════ Helpers ══════════════════ */
function base64ToUint8(b64: string): Uint8Array {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
}

function parseGrid(data: any): Uint16Array {
  const bytes = base64ToUint8(data.grid);
  if (data.version === 2) {
    const aligned = new ArrayBuffer(bytes.length);
    new Uint8Array(aligned).set(bytes);
    return new Uint16Array(aligned);
  }
  const grid = new Uint16Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    const v = bytes[i];
    if (v >= 1 && v <= 14) grid[i] = 1 << (v - 1);
  }
  return grid;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m > 0) return `${m}m ${s.toFixed(1)}s`;
  return `${s.toFixed(1)}s`;
}

function gridDist(
  a: { gx: number; gy: number },
  b: { gx: number; gy: number },
): number {
  return Math.sqrt((a.gx - b.gx) ** 2 + (a.gy - b.gy) ** 2);
}

function hexToRgb(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
}

/* ══════════════════ Vision helpers ══════════════════ */

/**
 * Bresenham line-of-sight for vision.
 * Walls always block.  Bushes block when the ward is outside a bush.
 */
function visionLoS(
  grid: Uint16Array,
  x0: number, y0: number,
  x1: number, y1: number,
  wardInBush: boolean,
): boolean {
  let dx = Math.abs(x1 - x0);
  let dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;
  let x = x0, y = y0;

  while (true) {
    // Skip the ward's own cell
    if (x !== x0 || y !== y0) {
      if (x < 0 || x >= GRID_RES || y < 0 || y >= GRID_RES) return false;
      const mask = grid[y * GRID_RES + x];
      if (mask & VISION_WALL_MASK) return false;
      if (!wardInBush && (mask & BUSH_BIT)) return false;
    }
    if (x === x1 && y === y1) return true;
    const e2 = 2 * err;
    if (e2 > -dy) { err -= dy; x += sx; }
    if (e2 < dx)  { err += dx; y += sy; }
  }
}

/**
 * Compute a vision bitmap for one team.
 * Returns a Uint8Array[GRID_RES²] where 1 = visible.
 */
function computeTeamVision(
  grid: Uint16Array,
  wards: Ward[],
  team: 'blue' | 'red',
): Uint8Array {
  const vision = new Uint8Array(GRID_RES * GRID_RES);
  const enemyTeam = team === 'blue' ? 'red' : 'blue';
  const enemyControlWards = wards.filter(w => w.team === enemyTeam && w.type === 'control');
  const teamWards = wards.filter(w => w.team === team);
  const rangeCeil = Math.ceil(WARD_VISION_RANGE_GRID);
  const rangeSq = WARD_VISION_RANGE_GRID * WARD_VISION_RANGE_GRID;

  for (const ward of teamWards) {
    // Stealth wards can be suppressed by enemy control wards
    if (ward.type === 'stealth') {
      let suppressed = false;
      for (const cw of enemyControlWards) {
        const dd = (ward.gx - cw.gx) ** 2 + (ward.gy - cw.gy) ** 2;
        if (dd <= rangeSq) { suppressed = true; break; }
      }
      if (suppressed) continue;
    }

    const wardInBush = (grid[ward.gy * GRID_RES + ward.gx] & BUSH_BIT) !== 0;

    for (let dy = -rangeCeil; dy <= rangeCeil; dy++) {
      for (let dx = -rangeCeil; dx <= rangeCeil; dx++) {
        if (dx * dx + dy * dy > rangeSq) continue;
        const tx = ward.gx + dx;
        const ty = ward.gy + dy;
        if (tx < 0 || tx >= GRID_RES || ty < 0 || ty >= GRID_RES) continue;
        const idx = ty * GRID_RES + tx;
        if (vision[idx]) continue; // already visible
        if (visionLoS(grid, ward.gx, ward.gy, tx, ty, wardInBush)) {
          vision[idx] = 1;
        }
      }
    }
  }
  return vision;
}

/* ══════════════════ Public types ══════════════════ */

export interface PathfindingMapHandle {
  resetView: () => void;
}

export interface PathfindingMapProps {
  champions: MapChampion[];
  selectedChampIdx: number | null;
  flashMode: boolean;
  onChampionsChange: (
    fn: MapChampion[] | ((prev: MapChampion[]) => MapChampion[]),
  ) => void;
  onSelectChampion: (idx: number | null) => void;
  onAddWaypoint: (
    champIdx: number,
    pos: { gx: number; gy: number },
    isFlash: boolean,
  ) => void;
  onRemoveWaypoint: (champIdx: number, wpIdx: number) => void;
  onRecalculateFullPath: (champIdx: number) => void;
  /** Callback fired when zoom or grid-cursor changes */
  onStatusChange?: (info: {
    zoom: number;
    cursorGx: number;
    cursorGy: number;
  }) => void;
  /* ── Wards ── */
  wards: Ward[];
  wardMode: { type: 'stealth' | 'control'; team: 'blue' | 'red' } | null;
  showVision: boolean;
  onPlaceWard: (gx: number, gy: number) => void;
  onRemoveWard: (wardId: string) => void;
}

/* ══════════════════ Component ══════════════════ */

const PathfindingMap = forwardRef<PathfindingMapHandle, PathfindingMapProps>(
  (
    {
      champions,
      selectedChampIdx,
      flashMode,
      onChampionsChange,
      onSelectChampion,
      onAddWaypoint,
      onRemoveWaypoint,
      onRecalculateFullPath,
      onStatusChange,
      wards,
      wardMode,
      showVision,
      onPlaceWard,
      onRemoveWard,
    },
    ref,
  ) => {
    /* ── Internal state ── */
    const [showOverlay] = useState(false);
    const [overlayOpacity] = useState(0.35);
    const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [spaceHeld, setSpaceHeld] = useState(false);
    const [cursorInfo, setCursorInfo] = useState({ gx: -1, gy: -1 });
    const [stageW, setStageW] = useState(800);
    const [stageH, setStageH] = useState(800);
    const [gridLoaded, setGridLoaded] = useState(false);

    /* ── Refs ── */
    const gridRef = useRef<Uint16Array | null>(null);
    const overlayRef = useRef<HTMLCanvasElement>(null);
    const pathCanvasRef = useRef<HTMLCanvasElement>(null);
    const visionCanvasRef = useRef<HTMLCanvasElement>(null);
    const wardsRef = useRef(wards);
    const viewportRef = useRef<HTMLDivElement>(null);
    const panningRef = useRef(false);
    const spaceRef = useRef(false);
    const lastPanRef = useRef({ x: 0, y: 0 });
    const draggingRef = useRef<number | null>(null);
    const championsRef = useRef(champions);

    /* Scrollbar drag state */
    const sbDragRef = useRef<{
      axis: 'x' | 'y';
      startClient: number;
      startPan: number;
    } | null>(null);

    const sRef = useRef({ zoom, pan, stageW, stageH });
    useEffect(() => {
      sRef.current = { zoom, pan, stageW, stageH };
    });
    useEffect(() => {
      championsRef.current = champions;
    });
    useEffect(() => {
      wardsRef.current = wards;
    });

    const { championsAssets } = useContext(GameAssetsContext) || {};

    /* ═══════════ Imperative handle ═══════════ */
    useImperativeHandle(ref, () => ({
      resetView: () => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
      },
    }));

    /* ═══════════ Report status changes ═══════════ */
    useEffect(() => {
      onStatusChange?.({
        zoom,
        cursorGx: cursorInfo.gx,
        cursorGy: cursorInfo.gy,
      });
    }, [zoom, cursorInfo]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ═══════════ Load walkability grid ═══════════ */
    useEffect(() => {
      fetch(`${API_BASE}/grid`)
        .then((r) => r.json())
        .then((data) => {
          gridRef.current = parseGrid(data);
          setGridLoaded(true);
          renderOverlay();
        })
        .catch((err) =>
          console.error('Failed to load walkability grid:', err),
        );
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* ═══════════ Viewport sizing ═══════════ */
    useEffect(() => {
      const el = viewportRef.current;
      if (!el) return;
      setStageW(el.clientWidth);
      setStageH(el.clientHeight);
      const obs = new ResizeObserver((entries) => {
        for (const e of entries) {
          setStageW(e.contentRect.width);
          setStageH(e.contentRect.height);
        }
      });
      obs.observe(el);
      return () => obs.disconnect();
    }, []);

    /* ═══════════ Render grid overlay ═══════════ */
    const renderOverlay = useCallback(() => {
      const canvas = overlayRef.current;
      const grid = gridRef.current;
      if (!canvas || !grid) return;
      const ctx = canvas.getContext('2d')!;
      const imgData = ctx.createImageData(GRID_RES, GRID_RES);
      const alpha = Math.round(overlayOpacity * 255);
      for (let i = 0; i < GRID_RES * GRID_RES; i++) {
        const mask = grid[i];
        if (mask === 0) continue;
        let r = 0,
          g = 0,
          b = 0,
          first = true;
        for (const layer of LAYERS_BY_PRIORITY) {
          if (!(mask & layer.bit)) continue;
          if (first) {
            r = layer.rgb[0];
            g = layer.rgb[1];
            b = layer.rgb[2];
            first = false;
          } else {
            r = r * 0.3 + layer.rgb[0] * 0.7;
            g = g * 0.3 + layer.rgb[1] * 0.7;
            b = b * 0.3 + layer.rgb[2] * 0.7;
          }
        }
        if (first) continue;
        const off = i * 4;
        imgData.data[off] = r;
        imgData.data[off + 1] = g;
        imgData.data[off + 2] = b;
        imgData.data[off + 3] = showOverlay ? alpha : 0;
      }
      ctx.putImageData(imgData, 0, 0);
    }, [overlayOpacity, showOverlay]);

    useEffect(() => {
      renderOverlay();
    }, [renderOverlay]);

    /* ═══════════ Render ALL path segments on canvas ═══════════ */
    const renderPaths = useCallback(() => {
      const canvas = pathCanvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d')!;
      ctx.clearRect(0, 0, GRID_RES, GRID_RES);

      for (const mc of champions) {
        if (!mc.showPath || mc.pathSegments.length === 0) continue;
        const [r, g, b] = hexToRgb(mc.color);

        for (const seg of mc.pathSegments) {
          if (seg.points.length < 2) continue;

          ctx.lineWidth = 2;
          ctx.shadowColor = mc.color;
          ctx.shadowBlur = 4;

          if (seg.isFlash) {
            // Dashed line for flash blinks
            ctx.setLineDash([4, 4]);
            ctx.strokeStyle = `rgba(255, 215, 0, 0.8)`;
            ctx.shadowColor = '#ffd700';
          } else {
            ctx.setLineDash([]);
            ctx.strokeStyle = `rgb(${r}, ${g}, ${b})`;
          }

          ctx.beginPath();
          ctx.moveTo(seg.points[0].x + 0.5, seg.points[0].y + 0.5);
          for (let i = 1; i < seg.points.length; i++) {
            ctx.lineTo(seg.points[i].x + 0.5, seg.points[i].y + 0.5);
          }
          ctx.stroke();
        }
      }
      ctx.setLineDash([]);
      ctx.shadowBlur = 0;
    }, [champions]);

    useEffect(() => {
      renderPaths();
    }, [renderPaths]);

    /* ═══════════ Render vision overlay on canvas ═══════════ */
    useEffect(() => {
      const canvas = visionCanvasRef.current;
      const grid = gridRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d')!;
      ctx.clearRect(0, 0, GRID_RES, GRID_RES);

      if (!showVision || wards.length === 0 || !grid) return;

      const blueV = computeTeamVision(grid, wards, 'blue');
      const redV = computeTeamVision(grid, wards, 'red');

      const imgData = ctx.createImageData(GRID_RES, GRID_RES);
      for (let i = 0; i < GRID_RES * GRID_RES; i++) {
        const off = i * 4;
        const bv = blueV[i];
        const rv = redV[i];
        if (!bv && !rv) continue;
        if (bv && rv) {
          imgData.data[off]     = 180; // purple
          imgData.data[off + 1] = 80;
          imgData.data[off + 2] = 220;
        } else if (bv) {
          imgData.data[off]     = 50;  // blue
          imgData.data[off + 1] = 120;
          imgData.data[off + 2] = 255;
        } else {
          imgData.data[off]     = 255; // red
          imgData.data[off + 1] = 70;
          imgData.data[off + 2] = 70;
        }
        imgData.data[off + 3] = 45;
      }
      ctx.putImageData(imgData, 0, 0);
    }, [wards, showVision, gridLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ═══════════ Coordinate conversion ═══════════ */
    function screenToGrid(
      clientX: number,
      clientY: number,
    ): { gx: number; gy: number } | null {
      const vp = viewportRef.current;
      if (!vp) return null;
      const s = sRef.current;
      const rect = vp.getBoundingClientRect();
      const vx = clientX - rect.left - vp.clientLeft;
      const vy = clientY - rect.top - vp.clientTop;
      const sx = (vx - s.pan.x) / s.zoom;
      const sy = (vy - s.pan.y) / s.zoom;
      const gx = Math.floor((sx / s.stageW) * GRID_RES);
      const gy = Math.floor((sy / s.stageH) * GRID_RES);
      if (gx < 0 || gx >= GRID_RES || gy < 0 || gy >= GRID_RES) return null;
      return { gx, gy };
    }

    /* ═══════════ Hit-tests ═══════════ */
    function hitTestChampion(
      pos: { gx: number; gy: number },
    ): number | null {
      let bestIdx: number | null = null;
      let bestDist = Infinity;
      for (let i = 0; i < championsRef.current.length; i++) {
        const gp = championsRef.current[i].gridPosition;
        if (!gp) continue;
        const d = gridDist(pos, gp);
        if (d < CHAMP_HIT_RADIUS && d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      return bestIdx;
    }

    /** Hit-test all waypoints, return champion index + waypoint index */
    function hitTestWaypoint(
      pos: { gx: number; gy: number },
    ): { champIdx: number; wpIdx: number } | null {
      for (let i = 0; i < championsRef.current.length; i++) {
        const wps = championsRef.current[i].waypoints;
        for (let j = 0; j < wps.length; j++) {
          if (gridDist(pos, wps[j]) < WP_HIT_RADIUS) {
            return { champIdx: i, wpIdx: j };
          }
        }
      }
      return null;
    }

    /** Hit-test wards, return the ward id */
    function hitTestWard(
      pos: { gx: number; gy: number },
    ): string | null {
      for (const w of wardsRef.current) {
        if (gridDist(pos, w) < WARD_HIT_RADIUS) return w.id;
      }
      return null;
    }

    /* ═══════════ Pointer handlers ═══════════ */
    function onPointerDown(e: React.PointerEvent) {
      const vp = viewportRef.current;
      if (!vp) return;

      // Pan: middle-click or space+left-click
      if (e.button === 1 || (e.button === 0 && spaceRef.current)) {
        panningRef.current = true;
        lastPanRef.current = { x: e.clientX, y: e.clientY };
        vp.setPointerCapture(e.pointerId);
        return;
      }

      const pos = screenToGrid(e.clientX, e.clientY);
      if (!pos) return;

      // LEFT CLICK
      if (e.button === 0) {
        // 0) Ward placement mode → place a ward
        if (wardMode) {
          onPlaceWard(pos.gx, pos.gy);
          return;
        }

        // 1) Hit-test champion markers → select + start drag
        const hitIdx = hitTestChampion(pos);
        if (hitIdx !== null) {
          onSelectChampion(hitIdx);
          setDraggingIdx(hitIdx);
          draggingRef.current = hitIdx;
          vp.setPointerCapture(e.pointerId);
          return;
        }

        // 2) Hit-test ward markers → remove that ward
        const wardHit = hitTestWard(pos);
        if (wardHit !== null) {
          onRemoveWard(wardHit);
          return;
        }

        // 3) Hit-test waypoint markers → remove that waypoint
        const wpHit = hitTestWaypoint(pos);
        if (wpHit !== null) {
          onRemoveWaypoint(wpHit.champIdx, wpHit.wpIdx);
          return;
        }

        // 4) Selected champion not yet placed → place it here
        if (
          selectedChampIdx !== null &&
          championsRef.current[selectedChampIdx]?.gridPosition === null
        ) {
          const selIdx = selectedChampIdx;
          onChampionsChange((prev) => {
            const u = [...prev];
            u[selIdx] = { ...u[selIdx], gridPosition: pos };
            return u;
          });
          return;
        }

        // 5) Click on empty map → deselect
        onSelectChampion(null);
        setDraggingIdx(null);
        draggingRef.current = null;
        return;
      }

      // RIGHT CLICK
      if (e.button === 2) {
        e.preventDefault();

        // Right-click on a ward → remove it
        const wardHit = hitTestWard(pos);
        if (wardHit !== null) {
          onRemoveWard(wardHit);
          return;
        }

        // Otherwise add waypoint for selected champion
        if (selectedChampIdx !== null) {
          const mc = championsRef.current[selectedChampIdx];
          if (mc?.gridPosition) {
            // Check if right-clicking on another champion → waypoint to them
            const targetIdx = hitTestChampion(pos);
            const dest =
              targetIdx !== null &&
              targetIdx !== selectedChampIdx &&
              championsRef.current[targetIdx]?.gridPosition
                ? championsRef.current[targetIdx].gridPosition!
                : pos;
            onAddWaypoint(selectedChampIdx, dest, flashMode);
          }
        }
      }
    }

    function onPointerMove(e: React.PointerEvent) {
      const gpos = screenToGrid(e.clientX, e.clientY);
      if (gpos) setCursorInfo(gpos);

      // Panning
      if (panningRef.current) {
        const dx = e.clientX - lastPanRef.current.x;
        const dy = e.clientY - lastPanRef.current.y;
        lastPanRef.current = { x: e.clientX, y: e.clientY };
        setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
        return;
      }

      // Dragging a champion
      if (draggingRef.current !== null && gpos) {
        const idx = draggingRef.current;
        onChampionsChange((prev) => {
          const u = [...prev];
          u[idx] = { ...u[idx], gridPosition: gpos };
          return u;
        });
      }
    }

    function onPointerUp() {
      panningRef.current = false;
      if (draggingRef.current !== null) {
        const idx = draggingRef.current;
        const mc = championsRef.current[idx];
        // Recalculate full path if champion has waypoints
        if (mc?.waypoints.length > 0 && mc?.gridPosition) {
          onRecalculateFullPath(idx);
        }
        setDraggingIdx(null);
        draggingRef.current = null;
      }
    }

    function handleContextMenu(e: React.MouseEvent) {
      e.preventDefault();
    }

    /* ═══════════ Wheel zoom + middle-click isolation ═══════════ */
    useEffect(() => {
      const vp = viewportRef.current;
      if (!vp) return;

      function handleWheel(e: WheelEvent) {
        e.preventDefault();
        const s = sRef.current;
        const rect = vp!.getBoundingClientRect();
        const mx = e.clientX - rect.left - vp!.clientLeft;
        const my = e.clientY - rect.top - vp!.clientTop;
        const sx = (mx - s.pan.x) / s.zoom;
        const sy = (my - s.pan.y) / s.zoom;
        const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
        const newZoom = Math.max(1, Math.min(12, s.zoom * factor));
        if (newZoom <= 1) {
          setPan({ x: 0, y: 0 });
          setZoom(1);
        } else {
          setPan({ x: mx - sx * newZoom, y: my - sy * newZoom });
          setZoom(newZoom);
        }
      }

      // Prevent browser auto-scroll on middle-click so it only pans the map
      function handleMouseDown(e: MouseEvent) {
        if (e.button === 1) e.preventDefault();
      }

      vp.addEventListener('wheel', handleWheel, { passive: false });
      vp.addEventListener('mousedown', handleMouseDown);
      return () => {
        vp.removeEventListener('wheel', handleWheel);
        vp.removeEventListener('mousedown', handleMouseDown);
      };
    }, []);

    /* ═══════════ Keyboard ═══════════ */
    useEffect(() => {
      function down(e: KeyboardEvent) {
        if ((e.target as HTMLElement).tagName === 'INPUT') return;
        if (e.key === ' ') {
          spaceRef.current = true;
          setSpaceHeld(true);
          e.preventDefault();
        }
      }
      function up(e: KeyboardEvent) {
        if (e.key === ' ') {
          spaceRef.current = false;
          setSpaceHeld(false);
        }
      }
      window.addEventListener('keydown', down);
      window.addEventListener('keyup', up);
      return () => {
        window.removeEventListener('keydown', down);
        window.removeEventListener('keyup', up);
      };
    }, []);

    /* ═══════════ Scrollbar drag handlers ═══════════ */
    const onSbPointerDown = useCallback(
      (axis: 'x' | 'y', e: React.PointerEvent) => {
        e.stopPropagation();
        e.preventDefault();
        const s = sRef.current;
        sbDragRef.current = {
          axis,
          startClient: axis === 'x' ? e.clientX : e.clientY,
          startPan: axis === 'x' ? s.pan.x : s.pan.y,
        };
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
      },
      [],
    );

    const onSbPointerMove = useCallback((e: React.PointerEvent) => {
      const d = sbDragRef.current;
      if (!d) return;
      e.stopPropagation();
      const s = sRef.current;
      const vpSize = d.axis === 'x' ? s.stageW : s.stageH;
      const maxPan = vpSize * (s.zoom - 1);
      const tRatio = 1 / s.zoom;
      const clientDelta =
        (d.axis === 'x' ? e.clientX : e.clientY) - d.startClient;
      const trackUsable = vpSize * (1 - tRatio);
      if (trackUsable <= 0) return;
      const panDelta = -(clientDelta / trackUsable) * maxPan;
      const newPan = Math.max(-maxPan, Math.min(0, d.startPan + panDelta));
      setPan((prev) =>
        d.axis === 'x' ? { ...prev, x: newPan } : { ...prev, y: newPan },
      );
    }, []);

    const onSbPointerUp = useCallback((e: React.PointerEvent) => {
      if (sbDragRef.current) {
        e.stopPropagation();
        sbDragRef.current = null;
      }
    }, []);

    /* ═══════════ Scrollbar thumb calculations ═══════════ */
    const scrollVisible = zoom > 1;
    const thumbRatio = 1 / zoom;
    const maxPanX = stageW * (zoom - 1);
    const maxPanY = stageH * (zoom - 1);
    const scrollXFrac =
      maxPanX > 0 ? Math.max(0, Math.min(1, -pan.x / maxPanX)) : 0;
    const scrollYFrac =
      maxPanY > 0 ? Math.max(0, Math.min(1, -pan.y / maxPanY)) : 0;
    const thumbPctH = `${thumbRatio * 100}%`;
    const thumbPctV = `${thumbRatio * 100}%`;
    const thumbLeftPct = `${scrollXFrac * (1 - thumbRatio) * 100}%`;
    const thumbTopPct = `${scrollYFrac * (1 - thumbRatio) * 100}%`;

    /* ═══════════ Helpers ═══════════ */
    function gridToStagePercent(gx: number, gy: number) {
      return {
        left: `${(gx / GRID_RES) * 100}%`,
        top: `${(gy / GRID_RES) * 100}%`,
      };
    }

    /* ═══════════ Render ═══════════ */
    return (
      <div
        ref={viewportRef}
        className={`pf-viewport${spaceHeld ? ' pan-mode' : ''}${
          draggingIdx !== null ? ' dragging-champ' : ''
        }${flashMode ? ' flash-mode' : ''}${wardMode ? ' ward-mode' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onContextMenu={handleContextMenu}
      >
        <div
          className="pf-stage"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          }}
        >
          <img
            className="pf-map"
            src="/Images/riftmap.jpg"
            alt="Summoner's Rift"
            draggable={false}
          />
          <canvas
            ref={overlayRef}
            className="pf-overlay-canvas"
            width={GRID_RES}
            height={GRID_RES}
          />
          <canvas
            ref={visionCanvasRef}
            className="pf-vision-canvas"
            width={GRID_RES}
            height={GRID_RES}
          />
          <canvas
            ref={pathCanvasRef}
            className="pf-path-canvas"
            width={GRID_RES}
            height={GRID_RES}
          />

          {/* Flash range indicator */}
          {flashMode &&
            selectedChampIdx !== null &&
            (() => {
              const mc = champions[selectedChampIdx];
              if (!mc?.gridPosition) return null;
              const lastPt =
                mc.waypoints.length > 0
                  ? mc.waypoints[mc.waypoints.length - 1]
                  : mc.gridPosition;
              const sizePct = ((FLASH_RANGE_GRID * 2) / GRID_RES) * 100;
              return (
                <div
                  className="pf-flash-range"
                  style={{
                    left: `${(lastPt.gx / GRID_RES) * 100}%`,
                    top: `${(lastPt.gy / GRID_RES) * 100}%`,
                    width: `${sizePct}%`,
                    height: `${sizePct}%`,
                  }}
                />
              );
            })()}

          {/* Ward markers */}
          {wards.map((w) => {
            const isSuppressed =
              w.type === 'stealth' &&
              wards.some(
                (cw) =>
                  cw.type === 'control' &&
                  cw.team !== w.team &&
                  (cw.gx - w.gx) ** 2 + (cw.gy - w.gy) ** 2 <=
                    WARD_VISION_RANGE_GRID * WARD_VISION_RANGE_GRID,
              );
            return (
              <div
                key={w.id}
                className={`pf-ward-marker pf-ward-${w.team}${
                  w.type === 'control' ? ' pf-ward-control' : ''
                }${isSuppressed ? ' pf-ward-suppressed' : ''}`}
                style={gridToStagePercent(w.gx, w.gy)}
                title={`${w.team} ${w.type} ward${isSuppressed ? ' (suppressed)' : ''}`}
              >
                <div className="pf-ward-icon">
                  {w.type === 'control' ? '👁' : '◉'}
                </div>
              </div>
            );
          })}

          {/* Waypoint markers */}
          {champions.map((mc, champIdx) => {
            if (!mc.showPath || mc.waypoints.length === 0) return null;
            return mc.waypoints.map((wp, wpIdx) => {
              const isLast = wpIdx === mc.waypoints.length - 1;
              return (
                <div
                  key={`wp-${champIdx}-${wpIdx}`}
                  className={`pf-wp-marker${isLast ? ' pf-wp-dest' : ''}${
                    wp.isFlash ? ' pf-wp-flash' : ''
                  }`}
                  style={gridToStagePercent(wp.gx, wp.gy)}
                >
                  <div
                    className="pf-wp-ring"
                    style={{ borderColor: mc.color }}
                  >
                    <span className="pf-wp-num">
                      {wp.isFlash ? '⚡' : wpIdx + 1}
                    </span>
                  </div>
                </div>
              );
            });
          })}

          {/* Champion markers */}
          {champions.map((mc, idx) => {
            if (!mc.gridPosition) return null;
            const isSelected = selectedChampIdx === idx;
            const isDragging = draggingIdx === idx;
            const champImg =
              championsAssets?.[mc.champion.name]?.championImageUrl ||
              mc.champion.imageUrl;
            const ms = getChampionMS(mc);
            const hasTime = mc.travelTimeSeconds !== null && mc.showPath;
            return (
              <div
                key={mc.champion.id + '-' + idx}
                className={`pf-champ-marker${
                  isSelected ? ' pf-champ-selected' : ''
                }${isDragging ? ' pf-champ-dragging' : ''}`}
                style={gridToStagePercent(
                  mc.gridPosition.gx,
                  mc.gridPosition.gy,
                )}
                title={`${mc.champion.name} (Lvl ${
                  mc.champion.level
                }) — ${Math.round(ms)} MS`}
              >
                <div
                  className="pf-champ-ring"
                  style={{ borderColor: mc.color }}
                >
                  <img
                    src={champImg}
                    alt={mc.champion.name}
                    className="pf-champ-img"
                    draggable={false}
                  />
                </div>
                <span className="pf-champ-label">{mc.champion.name}</span>
                <span className="pf-champ-ms-label">{Math.round(ms)}</span>
                {hasTime && (
                  <span
                    className="pf-champ-time-label"
                    style={{ borderColor: mc.color }}
                  >
                    {formatTime(mc.travelTimeSeconds!)}
                  </span>
                )}
                {mc.pathLoading && (
                  <span className="pf-champ-time-label pf-champ-loading">
                    ...
                  </span>
                )}
                {isSelected && (
                  <div
                    className="pf-champ-highlight"
                    style={{ borderColor: mc.color }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* ═══════ Scrollbar overlays ═══════ */}
        <div className={`pf-scrollbar-v${scrollVisible ? ' visible' : ''}`}>
          <div className="pf-scrollbar-track" />
          <div
            className={`pf-scrollbar-thumb${
              sbDragRef.current?.axis === 'y' ? ' active' : ''
            }`}
            style={{ height: thumbPctV, top: thumbTopPct }}
            onPointerDown={(e) => onSbPointerDown('y', e)}
            onPointerMove={onSbPointerMove}
            onPointerUp={onSbPointerUp}
          />
        </div>
        <div className={`pf-scrollbar-h${scrollVisible ? ' visible' : ''}`}>
          <div className="pf-scrollbar-track" />
          <div
            className={`pf-scrollbar-thumb${
              sbDragRef.current?.axis === 'x' ? ' active' : ''
            }`}
            style={{ width: thumbPctH, left: thumbLeftPct }}
            onPointerDown={(e) => onSbPointerDown('x', e)}
            onPointerMove={onSbPointerMove}
            onPointerUp={onSbPointerUp}
          />
        </div>
      </div>
    );
  },
);

PathfindingMap.displayName = 'PathfindingMap';
export default PathfindingMap;
